import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { NodeEntry } from '@alfresco/js-api';
import { EMPTY } from 'rxjs';
import { map, filter, switchMap, take, tap, catchError } from 'rxjs/operators';

import {
  ASSIGN_WORKFLOW_STEP,
  AssignWorkflowStepAction,
  CANCEL_WORKFLOW,
  REASSIGN_TASK,
  REASSIGN_WORKFLOW,
  CancelWorkflowAction,
  ValidateAction,
  VALIDATE,
  ReassignWorkflowAction,
  ViewReassignTaskAction,
  StartProcessAction,
  START_PROCESS,
  EditDocumentAction,
  EDIT_DOCUMENT,
  SnackbarErrorAction,
} from '../actions/app.custom-actions';
import { WkReassignTaskDialogComponent } from '../../dialogs/wk-reassign-task/wk-reassign-task.dialog';
import { CustomContentManagementService } from '../../services/custom-content-management.service';
import { AppStore } from '../states/app.state';
import {
  getAppSelection,
  getSelectedRowsDataState,
} from '../selectors/app.custom-selectors';
import { WkAppConfigService } from '../../services/wk-app-config.service';
import { WkReassignTaskService } from '../../services/wk-reassign-task.service';
import { WkTaskListService } from '../../services/wk-task-list.service';
import { NodeInfoService } from '../../services/node.service';
import { AppConstants } from '../../components/shared/constants/app-constants';
import { WkValidationService } from '../../services/wk-validation.service';
import { CommonConstants } from '../../shared';

@Injectable()
export class NodeWorkflowEffects {
  private static readonly REASSIGN_WORKFLOW_DIALOG_PANEL_CLASS =
    'save-version-dialog-panel';
  'reassign-task-dialog-panel';
  private static readonly FETCH_STEPS_ERROR_KEY =
    'TASK_REASSIGNMENT.DIALOG.ERRORS.FETCH_STEPS';
  private readonly router: Router;
  private readonly appConfigService: WkAppConfigService;
  private readonly customContentService: CustomContentManagementService;
  private readonly wkReassignTaskService: WkReassignTaskService;
  private readonly wkTaskListService: WkTaskListService;
  private readonly nodeInfoService: NodeInfoService;
  private readonly dialogRef: MatDialog;
  private readonly validationService: WkValidationService;

  constructor(
    injector: Injector,
    private readonly store: Store<AppStore>,
    private readonly actions$: Actions
  ) {
    this.router = injector.get<Router>(Router);
    this.appConfigService =
      injector.get<WkAppConfigService>(WkAppConfigService);
    this.customContentService = injector.get<CustomContentManagementService>(
      CustomContentManagementService
    );
    this.wkReassignTaskService = injector.get<WkReassignTaskService>(
      WkReassignTaskService
    );
    this.wkTaskListService = injector.get<WkTaskListService>(WkTaskListService);
    this.nodeInfoService = injector.get<NodeInfoService>(NodeInfoService);
    this.dialogRef = injector.get<MatDialog>(MatDialog);
    this.validationService =
      injector.get<WkValidationService>(WkValidationService);
  }
  private static readonly REASSIGN_WORKFLOW_DIALOG_WIDTH = '900px';

  startProcessAction$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<StartProcessAction>(START_PROCESS),
        filter(() => this.appConfigService.apsEnabled()),
        map((action) => {
          if (action.payload && action.payload.length > 0) {
            this.nodeInfoService.startProcess(action.payload);
          } else {
            this.store
              .select(getAppSelection)
              .pipe(take(1))
              .subscribe((selection) => {
                if (selection && !selection.isEmpty) {
                  this.nodeInfoService.startProcess(selection.nodes);
                }
              });
          }
        })
      ),
    { dispatch: false }
  );

  EditDocumentAction$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<EditDocumentAction>(EDIT_DOCUMENT),
        map((action) => {
          // get the selection from the store
          this.store
            .select(getAppSelection)
            .pipe(take(1))
            .subscribe((selection) => {
              // take the node reference
              const node = selection.file?.entry;
              // get the document extension
              const fileExtension = node?.name.toLowerCase().split('.').pop();
              // check if is a valid extension for CKE
              const isSupportedExtension = this.appConfigService
                .get<string[]>('ckeSupportedExtension')
                .includes(fileExtension ?? '');
              // get the checkout status node
              const checkOutNodeAspect = node?.aspectNames?.find(
                (aspect: string) =>
                  aspect === AppConstants.CHECKEDOUT_ASPECT ||
                  aspect === AppConstants.LOCKED_ASPECT
              );
              // check if document extension is supported
              if (isSupportedExtension) {
                // if is supported
                if (!checkOutNodeAspect) {
                  // if document is not in checkout
                  this.customContentService
                    .checkOutNodeForEdition(node?.id ?? '')
                    .then((workingCopyId: string) => {
                      // go to edit document
                      this.customContentService.navigateToEditComponent(
                        action.payload.currentPath,
                        node?.parentId ?? '',
                        node?.id ?? '',
                        workingCopyId
                      );
                    })
                    .catch(() =>
                      this.store.dispatch(
                        new SnackbarErrorAction(
                          'APP.NOTIFICATION.CHECKOUT_ERROR'
                        )
                      )
                    );
                } else {
                  // if document is in checkout
                  this.customContentService.navigateToEditComponent(
                    action.payload.currentPath,
                    node?.parentId ?? '',
                    node?.id ?? ''
                  );
                }
              } else {
                // if not supported
                this.customContentService
                  .checkOutNodeForEdition(node?.id ?? '')
                  .then((workingCopyId: string) => {
                    this.router.navigate(
                      [
                        `/all-docs-dashboard/preview/${
                          workingCopyId ? workingCopyId : node?.id
                        }`,
                      ],
                      {
                        queryParams: {
                          editMode: true,
                        },
                      }
                    );
                  })
                  .catch(() =>
                    this.store.dispatch(
                      new SnackbarErrorAction('APP.NOTIFICATION.CHECKOUT_ERROR')
                    )
                  );
              }
            });
        })
      ),
    { dispatch: false }
  );

  assignWorkflowStep$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<AssignWorkflowStepAction>(ASSIGN_WORKFLOW_STEP),
        filter(() => this.appConfigService.apsEnabled()),
        map((action) => {
          const openByMode = (nodesToProcess: NodeEntry[]) => {
            if (!nodesToProcess.length) {
              return;
            }

            const selectedNode = nodesToProcess[0] as NodeEntry & {
              entry?: NodeEntry;
            };
            const selectedNodeEntry = selectedNode?.entry ?? selectedNode;
            const selectedNodeStepId = this.getFirstPropertyValue(
              selectedNodeEntry,
              CommonConstants.stepId
            );

            const assignWorkflowMode =
              this.appConfigService.getCurrentSiteAssignWorkflowStepMode();

            if (
              assignWorkflowMode === 'taskDetail' &&
              nodesToProcess.length === 1 &&
              selectedNodeStepId
            ) {
              this.customContentService.assignWorkflowStep(selectedNode);
              return;
            }

            this.openWorkflowDialog(nodesToProcess);
          };

          if (action?.payload) {
            openByMode([action.payload]);
            return;
          }

          this.store
            .select(getAppSelection)
            .pipe(take(1))
            .subscribe((selection) => {
              const nodesToProcess = selection?.nodes?.length
                ? selection.nodes
                : selection?.file
                ? [selection.file]
                : [];

              openByMode(nodesToProcess);
            });
        })
      ),
    { dispatch: false }
  );

  cancelWorkflow$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<CancelWorkflowAction>(CANCEL_WORKFLOW),
        filter(() => this.appConfigService.apsEnabled()),
        map((action) => {
          if (action?.payload) {
            this.customContentService.cancelWorkflow(action.payload);
          } else {
            this.store
              .select(getAppSelection)
              .pipe(take(1))
              .subscribe((selection) => {
                if (selection?.file) {
                  this.customContentService.cancelWorkflow(selection.file);
                }
              });
          }
        })
      ),
    { dispatch: false }
  );

  validate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<ValidateAction>(VALIDATE),
        map((action) => {
          if (action?.payload) {
            this.validationService
              .getValidationStatus(action.payload.nodeIds)
              .subscribe();
          }
        })
      ),
    { dispatch: false }
  );

  reassignWorkflow$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<ReassignWorkflowAction>(REASSIGN_WORKFLOW),
        filter(() => this.appConfigService.apsEnabled()),
        map((action) => {
          const reassignNodeTask = (node: any) => {
            const nodeEntry = node?.entry ?? node;
            const sourceId = nodeEntry?.id;
            const taskStepId = this.getFirstPropertyValue(
              nodeEntry,
              CommonConstants.stepId
            );

            if (!sourceId || !taskStepId) {
              return;
            }

            this.wkReassignTaskService
              .checkCanReassignTask(sourceId, nodeEntry)
              .pipe(take(1))
              .subscribe((canReassign) => {
                if (!canReassign) {
                  return;
                }

                this.wkTaskListService
                  .getTasksDataSteps([taskStepId])
                  .pipe(
                    take(1),
                    catchError(() => {
                      this.store.dispatch(
                        new SnackbarErrorAction(
                          NodeWorkflowEffects.FETCH_STEPS_ERROR_KEY
                        )
                      );
                      return EMPTY;
                    })
                  )
                  .subscribe((response) => {
                    const baseDocData = this.buildWorkflowDialogData(nodeEntry);
                    if (!baseDocData) {
                      return;
                    }

                    const stepDef = response?.results?.[0];
                    const docData = {
                      ...baseDocData,
                      stepAssignee: stepDef?.assignee?.email ?? null,
                      stepName: stepDef?.current?.name ?? null,
                      workflowId:
                        stepDef?.processInstanceId ?? baseDocData.workflowId,
                    };

                    const { validDocuments, invalidDocuments } =
                      this.validateDocumentsCompatibility([docData]);

                    this.dialogRef.open(WkReassignTaskDialogComponent, {
                      data: {
                        invalidDocumentsList: invalidDocuments,
                        selected: validDocuments,
                      },
                      panelClass:
                        NodeWorkflowEffects.REASSIGN_WORKFLOW_DIALOG_PANEL_CLASS,
                      width: NodeWorkflowEffects.REASSIGN_WORKFLOW_DIALOG_WIDTH,
                    });
                  });
              });
          };

          if (action?.payload) {
            reassignNodeTask(action.payload);
            return;
          }

          this.store
            .select(getAppSelection)
            .pipe(take(1))
            .subscribe((selection) => {
              if (selection?.nodes?.length > 1) {
                // Build base document data for all selected nodes
                const baseDocuments = selection.nodes
                  .map((node) =>
                    this.buildWorkflowDialogData(node?.entry ?? node)
                  )
                  .filter(Boolean);

                if (baseDocuments.length > 0) {
                  // Separate documents that are part of a process from those that are not
                  const docsWithStep = baseDocuments.filter(
                    (doc) => doc.stepId
                  );
                  const docsWithoutStep = baseDocuments.filter(
                    (doc) => !doc.stepId
                  );

                  // If no documents have a step, validate and open dialog immediately
                  if (!docsWithStep.length) {
                    const { validDocuments, invalidDocuments } =
                      this.validateDocumentsCompatibility(baseDocuments);
                    this.dialogRef.open(WkReassignTaskDialogComponent, {
                      data: {
                        invalidDocumentsList: invalidDocuments,
                        selected: validDocuments,
                      },
                      panelClass:
                        NodeWorkflowEffects.REASSIGN_WORKFLOW_DIALOG_PANEL_CLASS,
                      width: NodeWorkflowEffects.REASSIGN_WORKFLOW_DIALOG_WIDTH,
                    });
                    return;
                  }

                  // Extract all step IDs to fetch task data (only for docs that are part of a process)
                  const stepIds = docsWithStep.map((doc) => doc.stepId);

                  this.wkTaskListService
                    .getTasksDataSteps(stepIds)
                    .pipe(
                      take(1),
                      catchError(() => {
                        this.store.dispatch(
                          new SnackbarErrorAction(
                            NodeWorkflowEffects.FETCH_STEPS_ERROR_KEY
                          )
                        );
                        return EMPTY;
                      })
                    )
                    .subscribe((response) => {
                      // Map step data by step ID for quick lookup
                      const stepDataMap = new Map();
                      response?.results?.forEach((stepDef: any) => {
                        const stepId =
                          stepDef?.id || stepDef?.key || stepDef?.taskId;
                        if (stepId) {
                          stepDataMap.set(String(stepId), stepDef);
                        }
                      });

                      // Enrich documents with actual step data (only those that have a step)
                      const enrichedDocuments = docsWithStep.map(
                        (baseDoc: any, index: number) => {
                          const stepDef =
                            stepDataMap.get(String(baseDoc.stepId)) ||
                            response?.results?.[index];
                          return {
                            ...baseDoc,
                            stepAssignee: stepDef?.assignee?.email ?? null,
                            stepName: stepDef?.current?.name ?? null,
                            workflowId: baseDoc.workflowId,
                            workflowDefinitionId:
                              stepDef?.processDefinitionId ??
                              baseDoc.workflowDefinitionId,
                            workflowName:
                              stepDef?.processName ?? baseDoc.workflowName,
                          };
                        }
                      );

                      // Rebuild allDocuments in original baseDocuments order
                      const enrichedMap = new Map(
                        enrichedDocuments.map((doc) => [doc.nodeId, doc])
                      );
                      const docsWithoutStepMap = new Map(
                        docsWithoutStep.map((doc) => [doc.nodeId, doc])
                      );
                      const allDocuments = baseDocuments.map(
                        (doc) =>
                          enrichedMap.get(doc.nodeId) ||
                          docsWithoutStepMap.get(doc.nodeId)
                      );

                      // Validate after enriching with actual step data
                      const { validDocuments, invalidDocuments } =
                        this.validateDocumentsCompatibility(allDocuments);

                      this.dialogRef.open(WkReassignTaskDialogComponent, {
                        data: {
                          invalidDocumentsList: invalidDocuments,
                          selected: validDocuments,
                        },
                        panelClass:
                          NodeWorkflowEffects.REASSIGN_WORKFLOW_DIALOG_PANEL_CLASS,
                        width:
                          NodeWorkflowEffects.REASSIGN_WORKFLOW_DIALOG_WIDTH,
                      });
                    });
                }
              } else if (selection?.nodes?.length === 1) {
                reassignNodeTask(selection.nodes[0]);
              } else if (selection?.file) {
                reassignNodeTask(selection.file);
              }
            });
        })
      ),
    { dispatch: false }
  );

  viewReassignTask$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<ViewReassignTaskAction>(REASSIGN_TASK),
        switchMap(() =>
          this.store.select(getSelectedRowsDataState).pipe(
            take(1),
            switchMap((selectedRows: any[] | undefined) =>
              this.store.select(getAppSelection).pipe(
                take(1),
                map((selection: any) => ({
                  selectedRows: selectedRows ?? [],
                  selection,
                }))
              )
            )
          )
        ),
        tap(({ selectedRows, selection }: any) => {
          const taskId = selectedRows?.[0]?.stepId;

          if (taskId && taskId !== 'EMPTY') {
            this.router.navigate(['/task-list', 'task-reassignment', taskId], {
              queryParams: { source: 'all-docs-dashboard' },
            });
            return;
          }

          const selectedNode = selection?.file ?? selection?.nodes?.[0];
          const nodeEntry = selectedNode?.entry ?? selectedNode;
          const fallbackTaskId = this.getFirstPropertyValue(
            nodeEntry,
            CommonConstants.stepId
          );

          if (!fallbackTaskId || fallbackTaskId === 'EMPTY') {
            return;
          }

          const source = this.router.url?.startsWith('/')
            ? this.router.url.substring(1)
            : this.router.url;

          this.router.navigate(
            ['/task-list', 'task-reassignment', fallbackTaskId],
            {
              queryParams: { source },
            }
          );
        })
      ),
    { dispatch: false }
  );

  private getFirstPropertyValue(node: any, property: string): string {
    const value = node?.properties?.[property];
    return Array.isArray(value) ? value[0] : value;
  }

  private isNodeLocked(node: any): boolean {
    return !!(
      node?.isLocked ||
      node?.aspectNames?.includes(AppConstants.LOCKED_ASPECT) ||
      (node?.properties && node?.properties['cm:lockType'])
    );
  }

  private openWorkflowDialog(nodes: any[]): void {
    const baseDocuments = nodes
      .map((node) => this.buildWorkflowDialogData(node?.entry ?? node))
      .filter(Boolean);

    if (!baseDocuments.length) {
      return;
    }

    // Separate documents that are part of a process from those that are not
    const docsWithStep = baseDocuments.filter((doc) => doc.stepId);
    const docsWithoutStep = baseDocuments.filter((doc) => !doc.stepId);

    // If no documents have a step, treat all as conflicting immediately
    if (!docsWithStep.length) {
      const { validDocuments, invalidDocuments } =
        this.validateDocumentsCompatibility(baseDocuments);
      this.dialogRef.open(WkReassignTaskDialogComponent, {
        data: {
          invalidDocumentsList: invalidDocuments,
          selected: validDocuments,
        },
        panelClass: NodeWorkflowEffects.REASSIGN_WORKFLOW_DIALOG_PANEL_CLASS,
        width: NodeWorkflowEffects.REASSIGN_WORKFLOW_DIALOG_WIDTH,
      });
      return;
    }

    // Extract all step IDs to fetch task data (only for docs that are part of a process)
    const stepIds = docsWithStep.map((doc) => doc.stepId);

    this.wkTaskListService
      .getTasksDataSteps(stepIds)
      .pipe(
        take(1),
        catchError(() => {
          this.store.dispatch(
            new SnackbarErrorAction(NodeWorkflowEffects.FETCH_STEPS_ERROR_KEY)
          );
          return EMPTY;
        })
      )
      .subscribe((response) => {
        // Map step data by step ID for quick lookup
        const stepDataMap = new Map();
        response?.results?.forEach((stepDef: any) => {
          const stepId = stepDef?.id || stepDef?.key || stepDef?.taskId;
          if (stepId) {
            stepDataMap.set(String(stepId), stepDef);
          }
        });

        // Enrich documents with actual step data (only those that have a step)
        const enrichedDocuments = docsWithStep.map(
          (baseDoc: any, index: number) => {
            const stepDef =
              stepDataMap.get(String(baseDoc.stepId)) ||
              response?.results?.[index];
            return {
              ...baseDoc,
              stepAssignee: stepDef?.assignee?.email ?? null,
              stepName: stepDef?.current?.name ?? null,
              workflowId: baseDoc.workflowId,
              workflowDefinitionId:
                stepDef?.processDefinitionId ?? baseDoc.workflowDefinitionId,
              workflowName: stepDef?.processName ?? baseDoc.workflowName,
            };
          }
        );

        // Rebuild allDocuments in original baseDocuments order
        const enrichedMap = new Map(
          enrichedDocuments.map((doc) => [doc.nodeId, doc])
        );
        const docsWithoutStepMap = new Map(
          docsWithoutStep.map((doc) => [doc.nodeId, doc])
        );
        const allDocuments = baseDocuments.map(
          (doc) =>
            enrichedMap.get(doc.nodeId) || docsWithoutStepMap.get(doc.nodeId)
        );

        // Validate after enriching with actual step data
        const { validDocuments, invalidDocuments } =
          this.validateDocumentsCompatibility(allDocuments);

        this.dialogRef.open(WkReassignTaskDialogComponent, {
          data: {
            invalidDocumentsList: invalidDocuments,
            selected: validDocuments,
          },
          panelClass: NodeWorkflowEffects.REASSIGN_WORKFLOW_DIALOG_PANEL_CLASS,
          width: NodeWorkflowEffects.REASSIGN_WORKFLOW_DIALOG_WIDTH,
        });
      });
  }

  private validateDocumentsCompatibility(documents: any[]): {
    validDocuments: any[];
    invalidDocuments: any[];
  } {
    if (documents.length === 0) {
      return { validDocuments: [], invalidDocuments: [] };
    }

    const validDocuments: any[] = [];
    const invalidDocuments: any[] = [];

    // Separate locked documents
    const lockedDocs: any[] = [];
    const unlockedDocs: any[] = [];

    documents.forEach((doc) => {
      if (doc.isLocked) {
        lockedDocs.push(doc);
      } else {
        unlockedDocs.push(doc);
      }
    });

    // Mark locked documents
    lockedDocs.forEach((doc) => {
      doc.invalidReason = 'locked';
    });

    // Separate unlocked docs into those in process and not in process
    const notInProcessDocs = unlockedDocs.filter((doc) => !doc.stepId);
    const docsInProcess = unlockedDocs.filter((doc) => doc.stepId);

    // Mark not-in-process documents
    notInProcessDocs.forEach((doc) => {
      doc.invalidReason = 'not-in-process';
    });

    // Check whether all in-process documents share the same workflow definition and step
    const uniqueGroups = new Set(
      docsInProcess.map((doc) => `${doc.workflowName}|${doc.stepName}`)
    );

    // In-process docs are only conflicting if they disagree with each other (multiple groups)
    if (uniqueGroups.size > 1) {
      docsInProcess.forEach((doc) => {
        doc.invalidReason = 'conflicting';
      });
    }

    // Separate valid in-process docs (those not marked conflicting)
    const validInProcess = docsInProcess.filter((doc) => !doc.invalidReason);
    validDocuments.push(...validInProcess);
    invalidDocuments.push(...documents.filter((doc) => doc.invalidReason));

    return { validDocuments, invalidDocuments };
  }

  private buildWorkflowDialogData(nodeEntry: any): any | null {
    const sourceId = nodeEntry?.id;
    if (!sourceId) {
      return null;
    }
    const taskStepId =
      this.getFirstPropertyValue(nodeEntry, CommonConstants.stepId) ?? null;

    return {
      nodeId: sourceId,
      stepId: taskStepId,
      stepAssignee: null,
      stepName: null,
      workflowId: this.getFirstPropertyValue(
        nodeEntry,
        CommonConstants.processId
      ),
      workflowDefinitionId: this.getFirstPropertyValue(
        nodeEntry,
        CommonConstants.processId
      ),
      workflowName: null,
      isLocked: this.isNodeLocked(nodeEntry),
      fileName: nodeEntry?.name ?? '',
      documentTitle:
        nodeEntry?.properties?.['cm:title'] ?? nodeEntry?.name ?? '',
      parentId: nodeEntry?.parentId ?? '',
      folderPath: nodeEntry?.path?.name ?? '',
      nodeType_id: nodeEntry?.nodeType ?? '',
      nodeType_name: nodeEntry?.nodeType ?? '',
      modifiedBy_id: nodeEntry?.modifiedByUser?.id ?? '',
      modifiedBy_name: nodeEntry?.modifiedByUser?.displayName ?? '',
      createdBy_id: nodeEntry?.createdByUser?.id ?? '',
      createdBy_name: nodeEntry?.createdByUser?.displayName ?? '',
      created_at: nodeEntry?.createdAt ?? '',
      updated_at: nodeEntry?.modifiedAt ?? '',
      workflowStarted: null,
      stepStarted: null,
      id: 0,
      path: nodeEntry?.path ?? {},
    };
  }
}
